import React, { useCallback, useEffect, useState } from 'react';
import PdfViewer from './PdfViewer';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'components';
import {
  setDisplayConfigOrder,
  setFundCompareTable,
  setFundData,
} from '../Slice/FundDataSlice';
import {
  getDisplayConfig,
  getfundDetails,
  getScoringMatrix,
} from '../Slice/FundSlice';
import {
  grabPortfolios,
  setPortfolioData,
} from 'modules/PacingPlan/slice/pacingPlanSlice';
import { setScoringMatrix } from '../Slice/ScoringMatrixSlice';
import { getTableData } from 'modules/home/slice/homeSlice';
import { reportException } from 'services/logging';

const allowedStatuses = [
  'Initial Review',
  'Pending Review',
  'Due Diligence',
  'CIO Approval',
];

const PdfViewerData = () => {
  const [isLoading, setLoading] = useState(false);
  const [isComponentLoad, setIsComponentLoad] = useState(false);
  const [fundCompareData, setFundCompareData] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [dataList, setDataList] = useState([]);
  const { id } = useParams();
  const dispatch = useDispatch();
  const { userData } = useSelector(({ login }) => login);
  const { fundData } = useSelector(({ fundData }) => fundData);

  const uniqueDropDownDocs =
    dataList &&
    dataList
      ?.filter((raw) => raw?.fund_id !== id)
      .map((doc) => ({
        value: doc?.fund_id,
        label: doc?.fund_name,
        assetClass: doc?.asset_class,
        status: doc?.fund_status,
      }));

  const getAutoSelectedFunds = (currentFund) => {
    if (!currentFund) return [];

    const statusAndAssetClassFilteredFunds = uniqueDropDownDocs.filter(
      (doc) =>
        doc.assetClass === currentFund.asset_class &&
        allowedStatuses.includes(doc.status)
    );
    if (statusAndAssetClassFilteredFunds.length > 0) {
      return statusAndAssetClassFilteredFunds.slice(0, 3);
    }
    return [];
  };

  const getGridData = useCallback(() => {
    let obj = {};
    obj.size = '';
    obj.status = '';
    obj.sortBy = '';
    obj.filterBy = '';
    obj.orderBy = 'DESC';
    obj.filterValue = '';
    obj.page = 1;
    obj.pageSize = 20;
    dispatch(getTableData(obj))
      .then((res) => {
        if (res.payload.status === 200) {
          setDataList(res.payload.data.funds);
        }
      })
      .catch((err) => {
        reportException(err, 'fetching grid data for pdf viewer data');
      });
  }, [dispatch]);

  useEffect(() => {
    const fundCompareDataList =
      fundData?.fund_compare_metadata_in_json?.fund_ids?.map(
        (raw) => raw?.fund_id
      );
    const preSelectedOptions = uniqueDropDownDocs?.filter((doc) =>
      fundCompareDataList?.includes(doc.value)
    );
    const currentFund = dataList?.find((fund) => fund.fund_id == id) || null;
    const needsReset =
      selectedOptions.length > 0 &&
      !selectedOptions.every((option) => preSelectedOptions.includes(option));

    if (needsReset) {
      setSelectedOptions([]);
    }
    if (fundCompareDataList?.length > 0) {
      setSelectedOptions(preSelectedOptions);
    } else if (currentFund) {
      const autoSelectedFunds = getAutoSelectedFunds(currentFund);
      setSelectedOptions(autoSelectedFunds);
    }
  }, [fundData, dataList, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch fund details
        const fundDetailsRes = await dispatch(getfundDetails(id));
        if (fundDetailsRes?.payload?.status === 200) {
          const fundData = fundDetailsRes.payload.data.data;
          dispatch(setFundData(fundData));
          dispatch(setFundCompareTable(fundData));
          // Fetch portfolio data
          const portfolioResponse = await dispatch(grabPortfolios({}));
          dispatch(setPortfolioData(portfolioResponse));

          // Fetch scoring matrix
          const scoringMatrixPayload = await dispatch(
            getScoringMatrix({ fund_ID: id, user_ID: userData?.user_id })
          );
          if (scoringMatrixPayload?.payload?.status === 200) {
            const matrixData =
              scoringMatrixPayload.payload.data.data.fund_score_in_json;
            dispatch(setScoringMatrix(matrixData));
          }
          await getGridData();
          const hostUrl = window.location.host;
          // Fetch display configuration
          const displayConfigRes = await dispatch(
            getDisplayConfig({ host: hostUrl })
          );
          if (displayConfigRes?.payload?.status === 200) {
            dispatch(setDisplayConfigOrder(displayConfigRes.payload.data));
            setIsComponentLoad(true);
          }
        }
      } catch (error) {
        reportException(error, 'fetching data for pdf viewer data');
        setIsComponentLoad(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, dispatch, userData]);

  return (
    <>
      {isLoading && <Loader />}
      {isComponentLoad && (
        <PdfViewer
          fundKey={id}
          fundCompareData={fundCompareData}
          directCall={1}
          SelectedOptions={selectedOptions}
        />
      )}
    </>
  );
};
export default PdfViewerData;
